import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
} from 'react-native';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { capitalize } from 'lodash';
import LottieView from 'lottie-react-native';
import { submitRating } from '../../actions/RoutineActions';
import FastImageView from '../../components/FastImageView';
import RoutineHappinessProgressBar from './RoutineHappinessProgressBar';
import animations from '../../theme/Animations';
import { isBlank } from '../../utils/BooleanUtility';
import memoize from 'lodash/memoize';
import { jsonParser } from '../../utils/JsonUtility';
import RemoteConfig from '../../utils/RemoteConfig';
import { REMOTE_CONFIG_KEYS } from '../../config/Constants';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getDate()}/${date.getMonth() + 1}`;
};

const RoutineHappinessUnit = ({ itemData: happinessCardData , isRoutineActive }) => {
  const { extraArguments: { happinessHistory = {}, currentDayId } = {} } = happinessCardData;
  const hasScoreForToday = () => {
    const today = moment().format('YYYYMMDD');
    return happinessHistory.hasOwnProperty(today);
  };
  const dispatch = useDispatch();

  const userScores = Object.values(happinessHistory).map((value) => value);
  const userSkinRatingDate = Object.keys(happinessHistory).map((key) => key);
  const [combinedScores, setCombinedScores] = useState(userScores);
  const [previousDates, setPreviousDates] = useState(userSkinRatingDate);
  const [isScoreAvailable, setIsScoreAvailable] = useState(hasScoreForToday());
  const [isPlaying, setIsPlaying] = useState(false);
  if (isBlank(userSkinRatingDate) && !isRoutineActive) return null;
  const handleEmojiPress = (index) => {
    const rating = index + 1;
    if (index >= 0 && index <= 2) {
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
    }
    dispatch(
      submitRating(currentDayId, rating, (success, data) => {
        if (success) {
          const { user_skin_happiness_score: { score, created_at } = {} } =
            data;
          setCombinedScores((prevScores) => [...prevScores, score]);
          setPreviousDates((prevDates) => [...prevDates, created_at]);
          setIsScoreAvailable(true);
        } else {
          console.error('API call failed');
        }
      }),
    );
    setTimeout(() => setIsPlaying(false), 2000);
  };

  const renderProgressBar = ({ item, index }) => {
    const progressBarDate = moment(previousDates[index]);
    const height = `${20 + ((item - 1) / 5) * 60}%`;
    const emojiUrl = Object.values(happinessCardData.arguments || {});
    const happinessUnitBarColors = jsonParser(RemoteConfig.getValue(REMOTE_CONFIG_KEYS.happiness_unit_progress_bar_colors));
    let barColor = happinessUnitBarColors.default;
    switch (item) {
      case 1:
        barColor = happinessUnitBarColors.very_bad;
        break;
      case 2:
        barColor = happinessUnitBarColors.bad;
        break;
      case 3:
        barColor = happinessUnitBarColors.okay;
        break;
      case 4:
        barColor = happinessUnitBarColors.great;
        break;
      case 5:
        barColor = happinessUnitBarColors.excellent;
        break;
      default:
        barColor = happinessUnitBarColors.default;
        break;
    }
    return (
      <RoutineHappinessProgressBar
        barColor={barColor}
        height={parseInt(height, 10)}
        emojiUrl={emojiUrl[item - 1]}
        dateLabel={formatDate(progressBarDate)}
      />
    );
  };

  const { title, body, arguments: pictureUrl = {} } = happinessCardData;
  const keyExtractor = (item, index) => index.toString();

  const RenderHappinessScores = useCallback(() => {
    if (!isScoreAvailable) {
      return Object.entries(pictureUrl || {}).map(([key, label], index) => (
        <TouchableOpacity
          key={key}
          style={styles.emojiWrapper}
          onPress={memoize(
            () => handleEmojiPress(index),
            () => [index],
          )}
        >
          <FastImageView
            source={{
              uri: label,
            }}
            style={styles.emojiImage}
          />
          <Text style={styles.emojiLabel}>{capitalize(key)}</Text>
        </TouchableOpacity>
      ));
    }
    return (
      <FlatList
        data={combinedScores}
        renderItem={renderProgressBar}
        keyExtractor={keyExtractor}
        horizontal
        showsHorizontalScrollIndicator={false}
      />
    );
  }, [combinedScores, isScoreAvailable]);

  return (
    <View style={styles.container}>
      <View>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subTitle}>{body}</Text>
        <View style={styles.emojiContainer}>
          {isRoutineActive ? (
            <RenderHappinessScores />
          ) : (
            <FlatList
              data={combinedScores}
              renderItem={renderProgressBar}
              keyExtractor={(item, index) => index.toString()}
              horizontal
              showsHorizontalScrollIndicator={false}
            />
          )}
        </View>
      </View>
      {isPlaying && (
        <LottieView
          source={animations.confettiAnimation}
          autoPlay
          loop={false}
          style={styles.lottieAnimation}
          duration={2000}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    borderRadius: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 10,
    fontFamily: 'Roboto',
    color: '#000000',
  },
  emojiContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 140,
    alignItems: 'center',
  },
  emojiWrapper: {
    alignItems: 'center',
  },
  emojiImage: {
    width: 50,
    height: 50,
    marginBottom: 5,
  },
  emojiLabel: {
    fontSize: 14,
    color: '#555',
    fontWeight: '400',
    fontFamily: 'Roboto-Regular',
  },
  progressContainer: {
    marginTop: 30,
    // backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
  },
  progressTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  progressContent: {
    alignItems: 'center',
    marginHorizontal: 10,
  },
  progressBar: {
    width: 40,
    height: 100,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
    marginVertical: 10,
    overflow: 'hidden',
    justifyContent: 'flex-end',
  },
  progressFill: {
    width: 32,
    borderRadius: 8,
    alignSelf: 'center',
  },
  dateLabel: {
    fontSize: 12,
    color: '#979BAA',
    marginTop: 5,
    fontWeight: '500',
    fontFamily: 'Roboto-Regular',
  },
  barEmoji: {
    position: 'absolute',
    width: 30,
    height: 30,
    zIndex: 1,
  },
  subTitle: {
    fontSize: 14,
    color: '#7B7B7B',
    marginBottom: 20,
    fontWeight: '400',
  },
  lottieAnimation: {
    position: 'absolute'
  }
});

export default RoutineHappinessUnit;
